'use strict';

const METHODS = {alipay: 'CNY', wechat: 'CNY', visa: 'HKD'};
const object = value => !!value && typeof value === 'object' && !Array.isArray(value);
const amount = value => typeof value === 'string' && /^[1-9][0-9]{0,14}$/.test(value) && Number.isSafeInteger(Number(value));
const id = value => typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(value);
const need = (ok, code) => { if (!ok) throw new Error(code); };

function selection(value) {
  need(object(value) && Object.keys(value).every(key => key === 'skuId' || key === 'activityId') &&
    /^(permanent-(2|3|5|10)|limited-step-[1-6])$/.test(value.skuId || ''), 'INVALID_PRICE_SELECTION');
  const limited = value.skuId.startsWith('limited-');
  need(limited ? id(value.activityId) : value.activityId == null, 'INVALID_PRICE_ACTIVITY');
  return {skuId: value.skuId, ...(limited ? {activityId: value.activityId} : {})};
}

function keyFor(value) {
  const selected = selection(value);
  return selected.skuId + ':' + (selected.activityId || '');
}

function empty(status) {
  return {status, amountMinor: null, currency: 'CNY', expiresAt: null, from: false, paymentMethods: []};
}

function checked(value, selected, at) {
  need(object(value) && value.schema === 1 && value.skuId === selected.skuId &&
    (value.activityId == null ? null : value.activityId) === (selected.activityId || null) && Array.isArray(value.offers) && value.offers.length <= 3,
  'INVALID_PRICE_RESPONSE');
  const seen = new Set();
  for (const offer of value.offers) {
    need(object(offer) && Object.prototype.hasOwnProperty.call(METHODS, offer.paymentMethod) &&
      !seen.has(offer.paymentMethod) && offer.currency === METHODS[offer.paymentMethod] &&
      id(offer.offerId) && amount(offer.amountMinor) && Number.isSafeInteger(offer.expiresAt) &&
      offer.expiresAt > at, 'INVALID_PRICE_OFFER');
    seen.add(offer.paymentMethod);
  }
  // Availability comes only from this validated SKU/activity response, never its pack count.
  const activeOffers = value.offers.filter(offer => offer.enabled !== false && offer.available !== false);
  const paymentMethods = Object.keys(METHODS).filter(method => activeOffers.some(offer => offer.paymentMethod === method));
  const cny = activeOffers.filter(offer => offer.currency === 'CNY');
  const expiresAt = activeOffers.length ? Math.min(...activeOffers.map(offer => offer.expiresAt)) : null;
  if (!cny.length) return {...empty('unavailable'), expiresAt, paymentMethods};
  const sorted = cny.slice().sort((a, b) => Number(a.amountMinor) - Number(b.amountMinor));
  return {status: 'ready', amountMinor: sorted[0].amountMinor, currency: 'CNY', expiresAt,
    from: sorted.some(offer => offer.amountMinor !== sorted[0].amountMinor), paymentMethods};
}

function confirmedPaymentMethods(entry, at = Date.now()) {
  if (!entry || !['ready', 'unavailable'].includes(entry.status) ||
    !Number.isSafeInteger(entry.expiresAt) || entry.expiresAt <= at ||
    !Array.isArray(entry.paymentMethods) || entry.paymentMethods.length > 3 ||
    new Set(entry.paymentMethods).size !== entry.paymentMethods.length ||
    entry.paymentMethods.some(method => !Object.prototype.hasOwnProperty.call(METHODS, method))) return [];
  return Object.keys(METHODS).filter(method => entry.paymentMethods.includes(method));
}

function formatCnyPrice(entry) {
  if (!entry || entry.status === 'loading') return '讀取價格…';
  if (entry.status === 'unavailable') return '暫無 CNY 報價';
  if (entry.status !== 'ready' || entry.currency !== 'CNY' || !amount(entry.amountMinor) || typeof entry.from !== 'boolean')
    return '價格暫時未能讀取';
  const digits = entry.amountMinor.padStart(3, '0');
  return 'CNY ¥' + digits.slice(0, -2) + '.' + digits.slice(-2) + (entry.from ? ' 起' : '');
}

function createPackagePrices({readOffers, onChange, now = Date.now, setTimer = setTimeout, clearTimer = clearTimeout}) {
  need([readOffers, onChange, now, setTimer, clearTimer].every(fn => typeof fn === 'function'), 'PRICE_DEPENDENCIES_REQUIRED');
  const cache = new Map(), inFlight = new Map();
  let visible = new Map(), active = false, disposed = false, timer = null, lastEmission = null;
  const expired = entry => entry && entry.expiresAt !== null && entry.expiresAt <= now();
  const cancel = () => { if (timer !== null) clearTimer(timer); timer = null; };

  function emit() {
    if (disposed || !active) return;
    const map = {};
    for (const key of [...visible.keys()].sort()) {
      const entry = cache.get(key) || empty('loading');
      map[key] = {...entry, paymentMethods: entry.paymentMethods.slice()};
    }
    const signature = JSON.stringify(map);
    if (signature === lastEmission) return;
    // Set before notifying: a React update may call show() again synchronously.
    lastEmission = signature;
    onChange(map);
  }

  function schedule() {
    cancel();
    if (disposed || !active) return;
    const deadlines = [...visible.keys()].map(key => cache.get(key)).filter(entry =>
      entry && ['ready', 'unavailable'].includes(entry.status) && entry.expiresAt !== null).map(entry => entry.expiresAt);
    if (!deadlines.length) return;
    timer = setTimer(() => {
      timer = null;
      if (disposed || !active) return;
      for (const [key, selected] of visible) if (expired(cache.get(key))) request(key, selected);
      emit(); schedule();
    }, Math.max(1, Math.min(2147483647, Math.min(...deadlines) - now())));
  }

  function request(key, selected) {
    if (disposed || inFlight.has(key)) return;
    cache.set(key, empty('loading'));
    const job = {};
    inFlight.set(key, job);
    Promise.resolve().then(() => {
      if (disposed) return null;
      if (!active || !visible.has(key)) {
        if (inFlight.get(key) === job) { inFlight.delete(key); cache.delete(key); }
        return null;
      }
      return readOffers({...selected});
    }).then(value => {
      if (disposed || inFlight.get(key) !== job) return;
      let entry;
      try { entry = checked(value, selected, now()); }
      catch (_) { entry = empty('error'); }
      inFlight.delete(key); cache.set(key, entry); emit(); schedule();
    }, () => {
      if (disposed || inFlight.get(key) !== job) return;
      inFlight.delete(key); cache.set(key, empty('error')); emit(); schedule();
    });
  }

  function show(selections) {
    if (disposed) return;
    need(Array.isArray(selections) && selections.length <= 5, 'PRICE_SELECTION_LIMIT');
    const next = new Map();
    for (const value of selections) {
      const selected = selection(value), key = keyFor(selected);
      need(!next.has(key), 'DUPLICATE_PRICE_SELECTION'); next.set(key, selected);
    }
    const prior = visible, wasActive = active;
    visible = next; active = true;
    for (const [key, selected] of visible) {
      const entry = cache.get(key), newlyVisible = !wasActive || !prior.has(key);
      if (!entry || expired(entry) || (newlyVisible && (entry.status === 'error' ||
        (entry.status === 'unavailable' && entry.expiresAt === null)))) request(key, selected);
    }
    emit(); schedule();
  }

  function refresh() {
    if (disposed || !active) return;
    for (const [key, selected] of visible) request(key, selected);
    emit(); schedule();
  }

  function pause() { active = false; cancel(); }
  function dispose() { disposed = true; active = false; cancel(); visible.clear(); cache.clear(); inFlight.clear(); }
  return Object.freeze({show, refresh, pause, dispose});
}

module.exports = {createPackagePrices, keyFor, formatCnyPrice, confirmedPaymentMethods};
